// react
import { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { css } from 'styled-components';

// radix
import * as RadixSlider from '@radix-ui/react-slider';

// components
import { BaseField, FieldProps, Option } from '@/components/atoms';

/**
 * Slider allows the user to select a value from a range.
 */
export const Slider = ({
  id,
  label,
  description,
  tooltip,
  isRequired,
  min = 0,
  max = 100,
  step = 1,
  isDisabled = false,
  isSegmented = false,
  isHideTitle = false,
  segmentLabels,
  lowLabel = 'Low',
  highLabel = 'High',
  options,
  value,
  onChange,
}: SliderProps) => {
  // memos
  const computedOptions = useMemo(
    () =>
      options
        ? options.sort((a, b) => (a?.order || 0) - (b?.order || 0))
        : undefined,
    [options],
  );
  const computedMin = useMemo(
    () => (computedOptions ? 0 : min),
    [min, computedOptions],
  );
  const computedMax = useMemo(
    () => (computedOptions ? computedOptions.length - 1 : max),
    [max, computedOptions],
  );
  const computedStep = useMemo(
    () => (computedOptions ? 1 : step),
    [step, computedOptions],
  );
  const computedValue = useMemo(() => {
    if (computedOptions && value !== undefined) {
      return computedOptions.findIndex((o) => o.value === value);
    }

    if (!computedOptions) {
      return Math.min(Math.max(value as number, computedMin), computedMax);
    }
    return undefined;
  }, [value, computedOptions, computedMin, computedMax]);

  const segments = useMemo(() => {
    if (!isSegmented) return undefined;

    const segments: React.ReactNode[] = [];
    for (let i = computedMin; i <= computedMax; i += computedStep) {
      const left = ((i - computedMin) / (computedMax - computedMin)) * 100;
      segments.push(
        <Segment
          key={i}
          $left={left}
          $isOn={!!computedValue && computedValue >= i * computedStep}
        />,
      );
    }
    return segments;
  }, [isSegmented, computedMin, computedMax, computedStep, computedValue]);

  useEffect(() => {
    if (!computedOptions && !value) onChange?.(min);
    if (computedOptions && !value) onChange?.(computedOptions[0]?.value);
  }, [computedOptions, value, min, onChange]);

  return (
    <BaseField
      id={id}
      label={label}
      description={description}
      tooltip={tooltip}
      isRequired={isRequired}
      isHideTitle={isHideTitle}
    >
      {computedOptions && (
        <SegmentLabel>
          {typeof value === 'string'
            ? computedOptions.find((o) => o.value === value)?.label
            : computedOptions[0]?.label}
        </SegmentLabel>
      )}
      <StyledSliderRoot
        value={computedValue ? [computedValue] : [0]}
        onValueChange={(val) => {
          const selectedValue = computedOptions
            ? computedOptions[val[0]]?.value
            : val[0];

          onChange?.(selectedValue);
        }}
        min={computedMin}
        max={computedMax}
        step={computedStep}
        disabled={isDisabled}
        $isLabelled={!!segmentLabels}
        aria-label='slider'
      >
        <StyledSliderTrack $isDisabled={isDisabled}>
          <StyledSliderRange $isDisabled={isDisabled} />
        </StyledSliderTrack>
        <SegmentWrapper>{segments}</SegmentWrapper>
        <StyledSliderThumb $isDisabled={isDisabled}>
          <StyledSliderThumbInner $isDisabled={isDisabled} />
        </StyledSliderThumb>
        {!segments && <Value>{computedValue}</Value>}
      </StyledSliderRoot>
      <SegmentFooter>
        <div>{lowLabel}</div>
        <div>{highLabel}</div>
      </SegmentFooter>
    </BaseField>
  );
};

interface DisabledProps {
  $isDisabled?: boolean;
}

interface StyledSliderRootProps {
  $isLabelled?: boolean;
}
const StyledSliderRoot = styled(RadixSlider.Root)<StyledSliderRootProps>`
  position: relative;
  display: flex;
  align-items: center;
  user-select: none;
  touch-action: none;
  height: 44px;
`;

const StyledSliderTrack = styled(RadixSlider.Track)<DisabledProps>`
  background: ${(props) => props.theme.color.neutral[40]};
  margin: 0 22px;
  position: relative;
  flex-grow: 1;
  border-radius: 9999px;
  height: 10px;

  ${(props) =>
    props.$isDisabled &&
    css`
      background: ${props.theme.color.neutral[40]};
    `}
`;

const StyledSliderRange = styled(RadixSlider.Range)<DisabledProps>`
  position: absolute;
  background: ${(props) => props.theme.color.primary[40]};
  border-radius: 9999px;
  height: 100%;

  ${(props) =>
    props.$isDisabled &&
    css`
      background: ${props.theme.color.neutral[50]};
    `}
`;

const StyledSliderThumb = styled(RadixSlider.Thumb)<DisabledProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: ${(props) => props.theme.color.primary[10]};
  box-shadow: ${(props) => props.theme.elevation[1]};
  border-radius: 9999px;

  &:hover {
    outline: 4px solid ${(props) => props.theme.color.primary[20]};
  }

  &:focus {
    border: 1px solid ${(props) => props.theme.color.primary[50]};
    outline: 4px solid ${(props) => props.theme.color.primary[20]};
  }

  ${(props) =>
    props.$isDisabled &&
    css`
      pointer-events: none;
    `}
`;

const StyledSliderThumbInner = styled.div<DisabledProps>`
  min-width: 8px;
  min-height: 8px;
  max-width: 8px;
  max-height: 8px;
  background: ${(props) => props.theme.color.primary[40]};
  border-radius: 9999px;
  ${(props) =>
    props.$isDisabled &&
    css`
      background: ${props.theme.color.neutral[50]};
    `}
`;

const SegmentWrapper = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  left: 0;
  right: 0;
  height: 100%;
  margin: 0 22px;
  box-sizing: border-box;
`;

interface SegmentProps {
  $left: number;
  $isOn: boolean;
}
const Segment = styled.div<SegmentProps>`
  position: absolute;
  width: 16px;
  min-width: 16px;
  max-width: 16px;
  height: 16px;
  min-height: 16px;
  max-height: 16px;
  border-radius: 99999px;
  left: ${(props) => props.$left}%;
  transform: translateX(-50%);
  background: ${(props) =>
    props.$isOn
      ? props.theme.color.primary[30]
      : props.theme.color.neutral[20]};
  box-shadow: ${(props) => props.theme.elevation[1]};
`;

interface SegmentLabelProps {
  $left?: number;
  $isStrong?: boolean;
}
const SegmentLabel = styled.div<SegmentLabelProps>`
  text-align: center;
  font: ${(props) =>
    props.theme.typography.body.md[props.$isStrong ? 'semibold' : 'regular']};
  color: ${(props) => props.theme.color.neutral[80]};
  left: ${(props) => props.$left}%;
  margin-top: ${(props) => props.theme.spacing[4]};
  height: 32px;
  min-height: 32px;
`;

const SegmentFooter = styled.div`
  display: flex;
  justify-content: space-between;
  font: ${(props) => props.theme.typography.body.sm.regular};
  color: ${(props) => props.theme.color.neutral[90]};
  margin-top: ${(props) => props.theme.spacing[2]};
`;

const Value = styled.div`
  position: absolute;
  top: -${(props) => props.theme.spacing[2]};
  right: 0;
  transform: translateY(-100%);
  font: ${(props) => props.theme.typography.body.sm.regular};
  color: ${(props) => props.theme.color.neutral[90]};
  text-align: right;
`;

interface SliderProps extends FieldProps<number | string> {
  min?: number;
  max?: number;
  step?: number;
  isDisabled?: boolean;
  isSegmented?: boolean;
  segmentLabels?: string[];
  lowLabel?: string;
  highLabel?: string;
  options?: Option[];
}

Slider.displayName = 'Slider';

export default Slider;
